'use client';

import { Box, css, HStack, VStack } from '@terra/ui-styled-system';
import { ArrowLeft02Icon, HelpCircleIcon, Search01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type UIEvent,
} from 'react';
import { Input } from '../../../components/input';
import { Spinner } from '../../../components/spinner';
import { LabelDefault, MetaDefault } from '../../../components/typography';
import { Scroll } from '../../Scroll';
import type {
  ChatMentionsMenuItemType,
  ChatMentionsMenuSection,
} from './types';
import { ChatMentionsMenuItem } from './ChatMentionsMenuItem';

const containerStyles = css({
  width: '100%',
  minWidth: '240px',
  maxWidth: '320px',
  overflow: 'hidden',
});
const emptyStateStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: '120px',
  py: 6,
  px: 3,
  color: 'fg.subtle',
  textAlign: 'center',
  gap: 2,
});

export type ChatMentionsMenuRef = {
  open: () => void;
  close: () => void;
};
export type ChatMentionsMenuProps = {
  sections: ChatMentionsMenuSection[];
  onItemSelect?: (item: ChatMentionsMenuItemType<unknown>) => void;
  selectedItemId?: string;
  searchPlaceholder?: string;
  onClose?: () => void;
  onBack?: () => void;
  showBackButton?: boolean;
  onNavigateToSubmenu?: (parentItem: ChatMentionsMenuItemType) => void;
  mode: 'compact' | 'default';
  searchFiltering?: 'client' | 'server';
  onSearchQueryChange?: (query: string) => void;
  onLoadMore?: () => void;
  canLoadMore?: boolean;
  isLoadingMore?: boolean;
  isLoadingList?: boolean;
  searchSessionKey?: string;
};
export const ChatMentionsMenu = forwardRef<
  ChatMentionsMenuRef,
  ChatMentionsMenuProps
>(
  (
    {
      sections,
      onItemSelect,
      selectedItemId,
      searchPlaceholder,
      onClose,
      onBack,
      showBackButton = false,
      onNavigateToSubmenu,
      mode,
      searchFiltering = 'client',
      onSearchQueryChange,
      onLoadMore,
      canLoadMore = false,
      isLoadingMore = false,
      isLoadingList = false,
      searchSessionKey,
    },
    ref,
  ) => {
    const t = useTranslations('designSystem.chat.chatMentionsMenu');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const listboxId = useId();
    const searchId = useId();

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 0);
        },
        close: () => {
          onClose?.();
        },
      }),
      [onClose],
    );
    const filteredSections = useMemo(() => {
      if (searchFiltering === 'server' || !searchQuery.trim()) {
        return sections;
      }

      const query = searchQuery.toLowerCase();

      return sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) =>
            item.label.toLowerCase().includes(query),
          ),
        }))
        .filter((section) => section.items.length > 0);
    }, [sections, searchFiltering, searchQuery]);
    const allItems = useMemo(() => {
      const items: Array<{
        item: ChatMentionsMenuItemType;
        sectionId: string;
      }> = [];

      filteredSections.forEach((section) => {
        section.items.forEach((item) => {
          items.push({ item, sectionId: section.id });
        });
      });

      return items;
    }, [filteredSections]);
    const callbackRef = useRef({
      allItems,
      activeIndex,
      onItemSelect,
      onNavigateToSubmenu,
      onClose,
      showBackButton,
      onBack,
      searchQuery,
    });

    callbackRef.current = {
      allItems,
      activeIndex,
      onItemSelect,
      onNavigateToSubmenu,
      onClose,
      showBackButton,
      onBack,
      searchQuery,
    };
    useEffect(() => {
      setActiveIndex(0);
    }, [searchSessionKey, searchQuery]);
    useEffect(() => {
      setActiveIndex((index) => {
        if (allItems.length === 0) {
          return 0;
        }

        return Math.min(index, allItems.length - 1);
      });
    }, [allItems.length]);
    useEffect(() => {
      setSearchQuery('');
      onSearchQueryChange?.('');
    }, [searchSessionKey, onSearchQueryChange]);
    const handleSearchQueryChange = useCallback(
      (value: string) => {
        setSearchQuery(value);
        onSearchQueryChange?.(value);
      },
      [onSearchQueryChange],
    );
    const handleScroll = useCallback(
      (event: UIEvent<HTMLDivElement>) => {
        if (!onLoadMore || !canLoadMore || isLoadingMore) {
          return;
        }

        const element = event.currentTarget;
        const distanceFromBottom =
          element.scrollHeight - element.scrollTop - element.clientHeight;

        if (distanceFromBottom <= 48) {
          onLoadMore();
        }
      },
      [canLoadMore, isLoadingMore, onLoadMore],
    );

    useEffect(() => {
      searchInputRef.current?.focus();
    }, [showBackButton, sections]);
    useEffect(() => {
      if (activeIndex >= 0 && scrollContainerRef.current) {
        scrollContainerRef.current.focus();
      }
    }, [activeIndex]);
    const handleItemClick = useCallback(
      (item: ChatMentionsMenuItemType) => {
        if (item.type === 'parent' || (item.hasSubmenu && item.children)) {
          onNavigateToSubmenu?.(item);

          return;
        }

        onItemSelect?.(item);
      },
      [onItemSelect, onNavigateToSubmenu],
    );
    const handleItemMouseEnter = useCallback((index: number) => {
      setActiveIndex(index);
    }, []);

    useEffect(() => {
      if (allItems.length > 0 && activeIndex >= 0) {
        const activeItemElement = containerRef.current?.querySelector(
          `[data-item-index="${activeIndex}"]`,
        ) as HTMLElement;

        if (activeItemElement) {
          activeItemElement.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
          });
        }
      }
    }, [activeIndex, allItems.length]);
    const handleNavigationKeyDown = useCallback((e: KeyboardEvent) => {
      if (e.key === 'Backspace') {
        const {
          showBackButton,
          onBack,
          searchQuery: query,
        } = callbackRef.current;

        if (showBackButton && onBack && query.length === 0) {
          e.preventDefault();
          e.stopPropagation();
          onBack();
        }

        return;
      }

      if (
        ![
          'ArrowDown',
          'ArrowUp',
          'ArrowLeft',
          'Home',
          'End',
          'Enter',
          'Escape',
        ].includes(e.key)
      ) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      switch (e.key) {
        case 'ArrowDown': {
          const { allItems } = callbackRef.current;

          setActiveIndex((prev) => (prev < allItems.length - 1 ? prev + 1 : 0));
          break;
        }

        case 'ArrowUp': {
          const { allItems } = callbackRef.current;

          setActiveIndex((prev) => (prev > 0 ? prev - 1 : allItems.length - 1));
          break;
        }

        case 'ArrowLeft': {
          const { showBackButton, onBack } = callbackRef.current;

          if (showBackButton && onBack) {
            onBack();
          }

          break;
        }

        case 'Enter': {
          const { allItems, activeIndex, onNavigateToSubmenu, onItemSelect } =
            callbackRef.current;

          if (allItems[activeIndex]) {
            const item = allItems[activeIndex].item;

            if (item.type === 'parent' || (item.hasSubmenu && item.children)) {
              onNavigateToSubmenu?.(item);
            } else {
              onItemSelect?.(item);
            }
          }

          break;
        }

        case 'Escape': {
          const { onClose } = callbackRef.current;

          onClose?.();
          break;
        }

        case 'Home': {
          setActiveIndex(0);
          break;
        }

        case 'End': {
          const { allItems } = callbackRef.current;

          setActiveIndex(allItems.length - 1);
          break;
        }
      }
    }, []);
    const handleScrollContainerClick = useCallback(() => {
      scrollContainerRef.current?.focus();
    }, []);

    return (
      <Box
        ref={containerRef}
        className={containerStyles}
        role="listbox"
        aria-label="Mentions menu"
        aria-describedby={searchId}
        aria-activedescendant={
          allItems[activeIndex]
            ? `item-${allItems[activeIndex].item.id}`
            : undefined
        }
        h="full"
        minH={0}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        onKeyDown={handleNavigationKeyDown}
      >
        <Box px="2" py="2" borderBottomWidth="1px" borderColor="border.subtle">
          {showBackButton ? (
            <HStack gap="1.5" alignItems="center">
              <Box
                onClick={onBack}
                role="button"
                aria-label={t('goBack')}
                color="fg.muted"
                cursor="pointer"
                flexShrink={0}
                display="flex"
                alignItems="center"
                _hover={{ color: 'fg.default' }}
              >
                <ArrowLeft02Icon size={12} />
              </Box>
              <Input
                ref={searchInputRef}
                autoFocus
                id={searchId}
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(event) =>
                  handleSearchQueryChange(event.target.value)
                }
                onKeyDown={handleNavigationKeyDown}
                size="xs"
                borderRadius="md"
                flex="1"
                role="searchbox"
                aria-label={t('searchMentions')}
                aria-controls={listboxId}
                autoComplete="off"
              />
            </HStack>
          ) : (
            <Box position="relative">
              <Box
                position="absolute"
                left="2"
                top="50%"
                transform="translateY(-50%)"
                color="fg.muted"
                pointerEvents="none"
              >
                <Search01Icon size={12} />
              </Box>
              <Input
                ref={searchInputRef}
                autoFocus
                id={searchId}
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(event) =>
                  handleSearchQueryChange(event.target.value)
                }
                onKeyDown={handleNavigationKeyDown}
                size="xs"
                pl="7"
                borderRadius="md"
                role="searchbox"
                aria-label={t('searchMentions')}
                aria-controls={listboxId}
                autoComplete="off"
              />
            </Box>
          )}
        </Box>
        <Scroll
          ref={scrollContainerRef}
          id={listboxId}
          role="group"
          aria-label={t('mentionOptions')}
          aria-busy={isLoadingList || isLoadingMore}
          tabIndex={0}
          flex={1}
          minHeight={0}
          onKeyDown={handleNavigationKeyDown}
          onClick={handleScrollContainerClick}
          onScroll={handleScroll}
        >
          {isLoadingList && allItems.length === 0 ? (
            <Box
              className={emptyStateStyles}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner size="sm" />
            </Box>
          ) : allItems.length > 0 ? (
            <VStack gap={0} width="full">
              {filteredSections.map((section, sectionIndex) => (
                <VStack key={section.id} width="full" gap={0}>
                  {section.title && !showBackButton && (
                    <Box
                      px="2"
                      py="1.5"
                      borderTopWidth={sectionIndex > 0 ? '1px' : '0'}
                      borderColor="border.subtle"
                      role="group"
                      aria-label={section.title}
                    >
                      <HStack gap="1.5" alignItems="center">
                        {section.titleIcon && (
                          <Box color="fg.muted">{section.titleIcon}</Box>
                        )}
                        <LabelDefault
                          textTransform="uppercase"
                          letterSpacing="wider"
                          color="fg.muted"
                        >
                          {section.title}
                        </LabelDefault>
                      </HStack>
                    </Box>
                  )}
                  {section.items.map((item, itemIndex) => {
                    let globalIndex = 0;

                    for (let i = 0; i < sectionIndex; i++) {
                      globalIndex += filteredSections[i].items.length;
                    }

                    globalIndex += itemIndex;
                    const isActive = globalIndex === activeIndex;
                    const isSelected = selectedItemId === item.id;

                    return (
                      <ChatMentionsMenuItem
                        key={`${section.id}-${item.id}`}
                        item={item}
                        isSelected={isSelected}
                        isActive={isActive}
                        onClick={() => handleItemClick(item)}
                        onMouseEnter={() => handleItemMouseEnter(globalIndex)}
                        data-item-index={globalIndex}
                        mode={mode}
                      />
                    );
                  })}
                </VStack>
              ))}
              {isLoadingMore ? (
                <Box px="2" py="2" display="flex" justifyContent="center">
                  <Spinner size="sm" />
                </Box>
              ) : null}
            </VStack>
          ) : (
            <Box className={emptyStateStyles}>
              <HelpCircleIcon size={24} opacity={0.4} />
              <VStack gap={0.5} alignItems="center">
                <MetaDefault fontWeight="medium" color="fg.muted">
                  {searchQuery.trim()
                    ? t('noResultsFound', { query: searchQuery.trim() })
                    : t('noResultsEmpty')}
                </MetaDefault>
                <MetaDefault color="fg.muted">
                  {searchQuery.trim()
                    ? t('noResultsDescription')
                    : t('noResultsEmptyDescription')}
                </MetaDefault>
              </VStack>
            </Box>
          )}
        </Scroll>
      </Box>
    );
  },
);
ChatMentionsMenu.displayName = 'ChatMentionsMenu';
